import styled from "styled-components";
import { GlobalStyleState, ProductState } from "../store/chart";
import { FontWeight, UIColor, UIText } from "../types";
import debounce from "lodash/debounce";
import { ComparisonPlaceholder } from "./Comparison";
import { FeatureName } from "./Feature";
import { MobileTableRow } from "./Table";
import { Text } from "./Text";
import { useCallback, useEffect, useRef, useState } from "react";
import { ReactComponent as CaretDownIcon } from "../assets/images/caret-down.svg";
import { trackShopSelectClick } from "../analytics";
import { ShopButton } from "./Product";

type MobileShopSelectSectionProps = {
  selectedProductColumn1: ProductState | null;
  selectedProductColumn2: ProductState | null;
  styling: GlobalStyleState;
  title: UIText;
  mobileTitleTextColor?: UIColor;
  mobileTitleRowColor?: UIColor;
  description: UIText;
  openOnLoad: boolean;
};

export function MobileShopSelectSection({
  selectedProductColumn1,
  selectedProductColumn2,
  styling,
  title,
  mobileTitleTextColor,
  mobileTitleRowColor,
  description,
  openOnLoad,
}: MobileShopSelectSectionProps) {
  const bodyRef = useRef<HTMLTableSectionElement>(null);
  const [maxHeight, setMaxHeight] = useState<number | null>(null);
  const [collapsed, setCollapsed] = useState(false);

  const handleResize = useCallback(() => {
    if (bodyRef.current) {
      const children = Array.from(bodyRef.current.children);
      const height = children.reduce(
        (acc, child) => acc + child.clientHeight,
        0,
      );
      setMaxHeight(height + 100);
    }
  }, []);

  const handleCollapseToggle = useCallback(() => {
    handleResize();
    setCollapsed((collapsed) => !collapsed);
  }, [handleResize]);

  useEffect(() => {
    window.addEventListener("resize", debounce(handleResize, 300));
    window.addEventListener("deviceorientation", debounce(handleResize, 300));
    // handleResize();
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("deviceorientation", handleResize);
    };
  }, [handleResize]);

  useEffect(() => {
    setCollapsed(!openOnLoad);
  }, [openOnLoad]);

  return (
    <div>
      <MobileFeatureSectionTitle
        font={title.font}
        textColor={mobileTitleTextColor}
        backgroundColor={mobileTitleRowColor}
        onClick={handleCollapseToggle}
      >
        <span>{title.value}</span>
        <CollapsibleIcon collapsed={collapsed} color={title.color?.hex}>
          <CaretDownIcon />
        </CollapsibleIcon>
      </MobileFeatureSectionTitle>
      <MobileFeatureSectionBody
        ref={bodyRef}
        collapsed={collapsed}
        style={
          collapsed ? { maxHeight: 0 } : maxHeight !== null ? { maxHeight } : {}
        }
      >
        <MobileFeatureRow>
          <MobileFeatureColumn>
            <FeatureName>{description.value}</FeatureName>
          </MobileFeatureColumn>
          {selectedProductColumn1 && (
            <ComparisonPlaceholder
              product={selectedProductColumn1}
              styling={styling}
            >
              {selectedProductColumn1.showShopSelectButton &&
                selectedProductColumn1.shopSelectButton && (
                  <ShopSelectContainer>
                    <ShopButton
                      as="a"
                      target="_blank"
                      href={selectedProductColumn1.shopSelectButton.url}
                      onClick={() =>
                        trackShopSelectClick(selectedProductColumn1)
                      }
                      textColor={styling.shopSelectButton.textColor}
                      backgroundColor={styling.shopSelectButton.backgroundColor}
                      borderColor={styling.shopSelectButton.borderColor}
                      hover={styling.shopSelectButton.hover}
                    >
                      {selectedProductColumn1.shopSelectButton.buttonCopy}
                    </ShopButton>
                  </ShopSelectContainer>
                )}
            </ComparisonPlaceholder>
          )}
          {selectedProductColumn2 && (
            <ComparisonPlaceholder
              product={selectedProductColumn2}
              styling={styling}
            >
              {selectedProductColumn2.showShopSelectButton &&
                selectedProductColumn2.shopSelectButton && (
                  <ShopSelectContainer>
                    <ShopButton
                      as="a"
                      target="_blank"
                      href={selectedProductColumn2.shopSelectButton.url}
                      onClick={() =>
                        trackShopSelectClick(selectedProductColumn2)
                      }
                      textColor={styling.shopSelectButton.textColor}
                      backgroundColor={styling.shopSelectButton.backgroundColor}
                      borderColor={styling.shopSelectButton.borderColor}
                      hover={styling.shopSelectButton.hover}
                    >
                      {selectedProductColumn2.shopSelectButton.buttonCopy}
                    </ShopButton>
                  </ShopSelectContainer>
                )}
            </ComparisonPlaceholder>
          )}
        </MobileFeatureRow>
      </MobileFeatureSectionBody>
    </div>
  );
}

const MobileFeatureSectionBody = styled.div<{ collapsed?: boolean }>`
  display: block;
  opacity: ${({ collapsed }) => (collapsed ? 0 : 1)};
  transition: all 0.5s;
`;

const MobileFeatureSectionTitle = styled(Text)<{ backgroundColor?: UIColor }>`
  &&& {
    font-weight: ${({ font: { weight = FontWeight.Black } = {} }) => weight};
    font-size: ${({ theme }) => theme.getFontSize(22)}px;
    font-style: italic;
    line-height: 1;
    margin: 0 -${({ theme }) => theme.getValue(8)}px;
    padding: ${({ theme }) =>
      `${theme.getValue(12)}px ${theme.getValue(24)}px`};
    color: ${({ theme, textColor: { hex = theme.colors.white } = {} }) => hex};
    background: ${({ backgroundColor, theme }) =>
      backgroundColor?.hex || theme.colors.green6};
    position: relative;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
`;

const MobileFeatureRow = styled(MobileTableRow)`
  border-top: 1px solid ${({ theme }) => theme.colors.black};
`;

const MobileFeatureColumn = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${({ theme }) => `${theme.getValue(24)}px 0`};
`;

const CollapsibleIcon = styled.span<{ collapsed?: boolean; color?: string }>`
  position: relative;
  font-size: ${({ theme }) => theme.getFontSize(14)}px;
  line-height: 1;
  width: ${({ theme }) => theme.getValue(14)}px;
  height: ${({ theme }) => theme.getValue(14)}px;
  transition: transform 0.5s;
  transform: ${({ collapsed }) => `rotate(${collapsed ? "-90deg" : "0deg"})`};

  & > svg {
    font-size: ${({ theme }) => theme.getFontSize(14)}px;
    width: ${({ theme }) => theme.getFontSize(14)}px;
    height: ${({ theme }) => theme.getFontSize(14)}px;
    fill: ${({ color, theme }) => color || theme.colors.white};
  }
`;

const ShopSelectContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1;
  padding: ${({ theme }) => `${theme.getValue(24)}px ${theme.getValue(6)}px`};
`;
